import { Menu } from 'lucide-react'
import { Link, useLocation } from 'react-router'

import { ProductNavTabs, ShellSlots } from '@/components/sidebar'
import { Button } from '@/components/ui/button'
import { useSidebar } from '@/components/ui/sidebar'
import { useIsMobile } from '@/hooks/use-mobile'
import { SidebarTab } from '@/lib/sidebarTab'
import { AccountMenu } from '../../../components/AccountMenu'
import { useFeatureFlag } from '../../../models/flag'
import { OrgStatus, useCurrentUser } from '../../../models/user'
import { ProductFlowNav } from '../../Public/marketing/components/ProductFlowNav'
import { GlobalSearch } from './GlobalSearch'
import { RemindersNavButton } from './Internal/Reminders/RemindersNavButton'
import { OrgSwitcher } from './OrgSwitcher'
import { useActiveSidebarTab } from './sidebar/ActiveSidebarTab'
import { isConsolePath, productTabs, SIDEBAR_TAB_LABEL } from './sidebar/navigation'

/** ProductFlowNav's height from md up, which the rail below it is pinned under. */
export const APP_TOP_BAR_HEIGHT = '4rem'

/** The signed-in app's top bar: ProductFlowNav's account variant. */
export function AppNavigation() {
  const { user } = useCurrentUser({ enabled: false })
  const { tab: activeTab, openTab } = useActiveSidebarTab()
  const onConsolePage = isConsolePath(useLocation().pathname)
  const { menuButton, org, actions, profile } = useAppNavigationPieces()

  return (
    <ProductFlowNav
      logoHref="/insure"
      rightAction={actions}
      account={{
        menuButton,
        org,
        // The console is a mode, not a fourth product: its pages have no strip, and on any
        // other page the strip stays, with no tab current while the rail shows the console.
        tabs:
          user && activeTab && !onConsolePage ? (
            <ProductNavTabs
              tabs={productTabs(user).map((tab) => ({ value: tab, label: SIDEBAR_TAB_LABEL[tab] }))}
              active={activeTab}
              onSelect={openTab}
            />
          ) : undefined,
        profile,
      }}
    />
  )
}

/** Insure's pieces of the top bar, inside the shared shell: its customer, actions and menu, without its own tabs. */
export function AppShellTopBarSlots() {
  const { menuButton, ...pieces } = useAppNavigationPieces()
  const { tab } = useActiveSidebarTab()
  // Only the console keeps its own phone menu; on Insure's other pages the shell's menu lists every product.
  return <ShellSlots {...pieces} menuButton={tab === SidebarTab.Console ? menuButton : undefined} />
}

/** The customer, page actions and menus Insure puts in whichever top bar it sits under. */
function useAppNavigationPieces() {
  const { user } = useCurrentUser({ enabled: false })
  const isMobile = useIsMobile()
  const { toggleSidebar } = useSidebar()
  const claimsEnabled = useFeatureFlag('ENABLE_CLAIMS')
  // The nav swaps sides on a console page: staff search for the org switcher and the customer's CTA.
  // The page decides, not the rail: switching the rail to the console on a customer page keeps the strip.
  const onConsolePage = isConsolePath(useLocation().pathname)

  function renderNavigationLink() {
    if (user?.currentOrg?.status === OrgStatus.QUOTING) {
      return (
        <Button asChild size="sm">
          <Link to="/quotes">View quotes</Link>
        </Button>
      )
    }

    if (claimsEnabled && user?.currentOrg?.status === OrgStatus.ACTIVE) {
      return (
        <Button asChild size="sm">
          <Link to="/insure/claims/new">Report a claim</Link>
        </Button>
      )
    }

    return null
  }

  return {
    actions: (
      <>
        {user?.isCaiAdmin && onConsolePage ? (
          <GlobalSearch />
        ) : (
          // Phones keep the row for the menu, the customer and the person; staff tools stay.
          <div className="max-md:hidden">{renderNavigationLink()}</div>
        )}
        {/* Staff reminders belong to the console. */}
        {user?.isCaiAdmin && onConsolePage && <RemindersNavButton />}
      </>
    ),
    menuButton: isMobile ? (
      <Button
        variant="ghost"
        size="icon"
        aria-label="Open menu"
        onClick={toggleSidebar}
        className="text-ink-600 size-7"
      >
        <Menu />
      </Button>
    ) : undefined,
    org: onConsolePage ? (
      <span className="text-ink-900 px-2 text-sm font-medium">{SIDEBAR_TAB_LABEL[SidebarTab.Console]}</span>
    ) : (
      <OrgSwitcher />
    ),
    profile: <AccountMenu />,
  }
}
